// packages/web-ui/src/hooks/useSkillsForSlash.ts
//
// Fetches the live skills list and returns the merged slash-command source —
// `[...SLASH_COMMANDS, ...buildSkillCommands(skills)]`. Composer threads the
// result into `<SlashMenu>` and `<GlobalPalette>` so every user-invocable
// skill appears as its own `/<skill-id>` entry alongside the static built-ins.
//
// Errors and the initial "loading" tick fall back silently to the static
// `SLASH_COMMANDS` array — the slash menu always renders something usable,
// even when the skills endpoint is unreachable.

import { useEffect, useMemo, useState } from 'react';
import type { SkillSummary } from '@tessa-lean/base/api-schemas/index.js';
import { getDefaultApiContext } from '../api/client.js';
import { listSkills } from '../api/clients/skills.js';
import {
  buildSkillCommands,
  SLASH_COMMANDS,
  type SlashCommand,
} from '../components/slash-palette/commands.js';

export interface UseSkillsForSlashResult {
  /** Merged source: static commands + one entry per user-invocable skill. */
  commands: ReadonlyArray<SlashCommand>;
  /** True while the first fetch is in flight. */
  loading: boolean;
}

export function useSkillsForSlash(): UseSkillsForSlashResult {
  const ctx = useMemo(getDefaultApiContext, []);
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    void listSkills(ctx).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setSkills(res.value.skills);
      }
      setLoading(false);
    });
    return (): void => {
      cancelled = true;
    };
  }, [ctx]);

  const commands = useMemo<ReadonlyArray<SlashCommand>>(
    () => [...SLASH_COMMANDS, ...buildSkillCommands(skills)],
    [skills],
  );

  return { commands, loading };
}
